import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Modal from 'live/components/ui/Modal'
import LiveTooltip from 'live/components/ui/tooltip'
import '../main/options/modalFull.scss'
import ProcessDataModal from './ProcessDataModal'
import { FileStatus, GroupModel, CheckedFileDVO } from './interface/Group'
import {parseCheckedFiles, getFailedUploads} from './utils'

interface Props {
    showModal: boolean
    setShowModal: (show: boolean) => void
    handleDiscard: () => void
    checkedFiles: FileStatus[]
    finishProcessData: () => void
    groupModel: GroupModel
    step: 'uploading' | 'checking' | 'summary'
}

export default function UploadSummaryModal({
    showModal,
    setShowModal,
    handleDiscard,
    checkedFiles,
    finishProcessData,
    groupModel,
    step
}: Props) {
    const [successfulUploads, setSuccessfulUploads] = useState<number>(0)
    const [checkedFilesDVO, setCheckedFilesDVO] = useState<CheckedFileDVO[]>([])
    const [showProcessDataModal, setShowProcessDataModal] = useState<boolean>(false)
    const [failedUploads, setFailedUploads] = useState<number>(0)

    useEffect(() => {
        let failedUploads = getFailedUploads(checkedFiles).length
        setFailedUploads(failedUploads)
        setSuccessfulUploads(checkedFiles.length - failedUploads)
        setCheckedFilesDVO([...parseCheckedFiles(checkedFiles)])
    }, [checkedFiles])

    const handleProcessData = () => {
        setShowModal(false)
        setShowProcessDataModal(true)
    }

    return (
        <>
            <Modal show={showModal} backdrop="static" keyboard={false} className="groupDatabaseModal">
                <Modal.Header>
                    <Modal.Title>
                        <b>Import Summary - Group Name</b>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <SummaryInfoContainer>
                        {failedUploads === checkedFilesDVO.length ? (
                            <div className="noMatch">
                                <i class="far fa-times-octagon"></i> All files were not uploaded due to incorrect
                                formatting or no matched database.
                            </div>
                        ) : (
                            <>
                                <span className={'successful'}>
                                    <b>{successfulUploads}</b> <i>files were upload into the database. </i>
                                </span>
                                {failedUploads > 0 && (
                                    <span className={'error'}>
                                        <b>{failedUploads}</b>{' '}
                                        <i>files could not be uploaded due to incorrect formatting.</i>
                                    </span>
                                )}
                            </>
                        )}
                    </SummaryInfoContainer>
                    <FileDataTable>
                        <table>
                            <thead>
                                <tr>
                                    <th>FILE NAME</th>
                                    <th>DATABASE</th>
                                    <th>DATABASE TAB</th>
                                    <th>FILE STATUS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {checkedFilesDVO.map(file => (
                                    <tr>
                                        <td>{file.fileName}</td>
                                        {
                                            file.database === 'No matched database'?<td style={{color:"var(--red-700)"}} >{file.database}</td>:<td>{file.database}</td>
                                        }
                                        <td>{file.databaseTab}</td>
                                        <td>
                                            {file.fileStatus === 'ok' && (
                                                <span className={'ok'}>OK</span>
                                            ) }
                                            {file.fileStatus === 'error' && (
                                                <>
                                                    <span className={'error'}>
                                                        Error
                                                        <LiveTooltip position="left" content={file.message} hide={false}>
                                                            <i className="far fa-info-circle"></i>
                                                        </LiveTooltip>
                                                    </span>
                                                </>
                                            )}

                                            {file.fileStatus === 'invalid' && (
                                                <>
                                                    <span className={'invalid'}>
                                                        Invalid
                                                        <LiveTooltip position="left" content={"No databases related to this worksheet"} hide={false}>
                                                            <i className="far fa-info-circle"></i>
                                                        </LiveTooltip>
                                                    </span>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </FileDataTable>
                </Modal.Body>
                <Modal.Footer>
                    <FooterContainer>
                        {failedUploads === checkedFilesDVO.length ? (
                            <button onClick={handleDiscard} className={'button--ok'}>
                                Ok
                            </button>
                        ) : (
                            <>
                                <button onClick={handleProcessData} className={'button--processData'}>
                                    Process Data
                                </button>
                                <button onClick={handleDiscard} className={'button--dicard'}>
                                    Discard
                                </button>
                            </>
                        )}
                    </FooterContainer>
                </Modal.Footer>
            </Modal>

            {step === 'summary' && showProcessDataModal && (
                <ProcessDataModal
                    filesToImport={checkedFilesDVO
                        .filter(
                            fileData => fileData.database !== 'No matched database' && fileData.fileStatus != 'error'
                        )
                        .map(fileData => ({
                            checkedFileDVO: fileData,
                            percent: 0,
                            error: false
                        }))}
                    showModal={showProcessDataModal}
                    setShowModal={setShowProcessDataModal}
                    finishProcessData={finishProcessData}
                />
            )}
        </>
    )
}

const FooterContainer = styled.div`
    .button {
        &--ok {
            width: 50px;
            height: 32px;
            color: var(--gray-0);
            background: var(--blue-500);
            border: none;
            border-radius: 3px;
            font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
            margin-right: 16px;
        }
        &--processData {
            width: 131px;
            height: 32px;
            color: var(--gray-0);
            background: var(--blue-500);
            border: none;
            border-radius: 3px;
            font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
            margin-right: 16px;
        }
        &--dicard {
            width: 79px;
            height: 32px;
            border: 1px solid var(--gray-400);
            border-radius: 3px;
            font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        }
    }
`
const SummaryInfoContainer = styled.div`
    font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
    font-size: 13px;
    margin-bottom: 16px;
    .noMatch {
        width: 100%;
        padding: 13px 16px;
        font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        color: var(--gray-800);
        border: 1px solid var(--red-500);
        background: var(--red-300);
        border-radius: 3px;
        i{

            color: var(--red-700);
            margin-right: 8px;
        }
    }
    .successful {
        color: var(--gray-800);
    }
    .error {
        color: var(--red-700);
    }
`

const FileDataTable = styled.div`
    width: 100%;
    max-height: 300px;
    overflow-y: auto;
    
    table{
        width: 100%;
    }
    th {
        color: var(--gray-800);
        padding: 4px 8px;
        background: var(--gray-100);
        font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        size: 11px;
        position: sticky;
        top: 0;
    }
    td {
        color: var(--gray-600);
        padding: 4px 8px;
        background: var(--gray-0);
        font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        font-size: 13px;
    }
    tr {
        border-bottom: 1px solid var(--gray-400);
    }

    .ok {
        font-size: 13px;
        color: #01c292;
    }

    .invalid {
        font-size: 13px;
        color: var(--grey-500);
        i {
            margin-left: 10px;
        }
    }

    .error {
        font-size: 13px;
        color: var(--red-700);
        i {
            margin-left: 10px;
            color: var(--red-700);
        }
    }
`
